<template>
  <main class="resource-detail-page">
    <article v-if="resource" class="resource-detail">
      <router-link to="/resources" class="resource-detail__back">
        <span aria-hidden="true">←</span> {{ $t('resources_page.back') }}
      </router-link>

      <header class="resource-detail__header">
        <div class="resource-detail__art" :class="{'resource-detail__art--mark': !resource.image}">
          <img
            v-if="resource.image"
            :src="imageFor(resource)"
            :alt="resource.title"
            width="840"
            height="540"
          >
          <span v-else>{{ resource.logoText }}</span>
        </div>
        <div class="resource-detail__intro">
          <span>{{ resource.category }}</span>
          <h1>{{ resource.title }}</h1>
          <p>{{ resource.summary }}</p>
        </div>
      </header>

      <div class="resource-detail__layout">
        <section class="resource-detail__description">
          <h2>{{ $t('resources_page.description') }}</h2>
          <p>{{ resource.description }}</p>
        </section>

        <aside class="resource-detail__facts">
          <section>
            <h2>{{ $t('resources_page.scenarios') }}</h2>
            <ul>
              <li v-for="scenario in resource.scenarios" :key="scenario">{{ scenario }}</li>
            </ul>
          </section>
        </aside>
      </div>
    </article>

    <section v-else class="resource-not-found">
      <h1>{{ $t('resources_page.not_found') }}</h1>
      <router-link to="/resources">{{ $t('resources_page.back') }}</router-link>
    </section>
  </main>
</template>

<script>
import { findResource } from '@/data/resources';

export default {
  name: 'ResourceDetail',
  computed: {
    resource() {
      return findResource(this.$route.params.slug);
    }
  },
  methods: {
    imageFor(resource) {
      return require(`@/assets/images/resources/${resource.image}`);
    },
    setPageTitle() {
      const title = this.resource ? this.resource.title : this.$t('resources_page.not_found');
      document.title = `${title} - Mltcloud`;
    }
  },
  watch: {
    '$route.params.slug'() {
      window.scrollTo(0, 0);
      this.setPageTitle();
    }
  },
  mounted() {
    window.scrollTo(0, 0);
    this.setPageTitle();
  }
};
</script>

<style lang="stylus" scoped>
/* Hallmark · macrostructure: Catalogue detail · theme: Cobalt · tone: professional and restrained
 * audience: smart-hardware partners · enrichment: source artwork + typographic marks
 * pre-emit critique: P5 H5 E4 S5 R5 V4 · contrast: pass (40–41)
 * nav/footer: existing project chrome · honest: pass (46) · chrome: pass (47)
 * tokens: pass (48) · responsive/mobile: pass (34, 49–57) · icons: pass (30)
 */
.resource-detail-page
  min-width 0
  padding var(--space-xl) clamp(var(--space-md), 5vw, var(--space-2xl)) var(--space-3xl)
  color var(--color-ink)
  background var(--color-paper)
  font-family var(--font-body)

.resource-detail
  max-width 76rem
  margin-inline auto

.resource-detail__back
  min-height 44px
  display inline-flex
  align-items center
  gap var(--space-xs)
  color var(--color-accent-dark)
  font-size var(--text-sm)
  font-weight 700
  text-decoration none
  white-space nowrap
  transition transform var(--dur-micro) var(--ease-out)

  &:hover
    transform translateX(-2px)

  &:active
    transform translateX(0)

  &:focus-visible
    outline 3px solid var(--color-focus)
    outline-offset 3px

.resource-detail__header
  padding-block var(--space-xl) var(--space-2xl)
  display grid
  gap var(--space-xl)
  border-bottom var(--rule-hair) solid var(--color-rule)

.resource-detail__art
  min-width 0
  aspect-ratio 14 / 9
  display grid
  place-items center
  overflow hidden
  color var(--color-accent-dark)
  background var(--color-paper-2)
  border var(--rule-hair) solid var(--color-rule)
  border-radius var(--radius-card)

  img
    display block
    width 100%
    height 100%
    object-fit cover

  > span
    max-width 12ch
    padding var(--space-xl)
    overflow-wrap anywhere
    text-align center
    font-family var(--font-display)
    font-size var(--text-2xl)
    font-weight 700
    line-height 1.12

.resource-detail__intro
  min-width 0
  align-self end

  > span
    color var(--color-accent-dark)
    font-family var(--font-mono)
    font-size var(--text-xs)
    letter-spacing 0.06em

  h1
    min-width 0
    margin var(--space-sm) 0 var(--space-md)
    overflow-wrap anywhere
    color var(--color-ink)
    font-family var(--font-display)
    font-size var(--text-detail-display)
    font-style normal
    font-weight 700
    line-height 1.1
    letter-spacing -0.035em

  p
    max-width 46rem
    margin 0
    color var(--color-ink-2)
    font-size var(--text-md)
    line-height 1.65

.resource-detail__layout
  padding-block var(--space-2xl)
  display grid
  gap var(--space-2xl)

.resource-detail__description
  h2,
  p
    margin 0

  h2
    margin-block-end var(--space-lg)
    font-family var(--font-display)
    font-size var(--text-xl)
    font-style normal
    font-weight 700
    letter-spacing -0.02em

  p
    max-width 66ch
    color var(--color-ink-2)
    font-size var(--text-md)
    line-height 1.8

.resource-detail__facts
  display grid
  align-content start
  gap var(--space-xl)

  section
    padding-block-start var(--space-md)
    border-top var(--rule-hair) solid var(--color-rule-strong)

  h2
    margin 0 0 var(--space-sm)
    color var(--color-muted)
    font-family var(--font-mono)
    font-size var(--text-xs)
    font-style normal
    font-weight 500
    letter-spacing 0.06em

  p,
  ul
    margin 0
    color var(--color-ink-2)
    font-size var(--text-base)
    line-height 1.7

  ul
    padding-inline-start var(--space-lg)

.resource-not-found
  max-width 50rem
  min-height 26rem
  margin-inline auto
  padding-block var(--space-3xl)

  h1
    color var(--color-ink)
    font-family var(--font-display)
    font-size var(--text-2xl)

  a
    min-height 44px
    display inline-flex
    align-items center
    color var(--color-accent-dark)
    font-weight 700
    white-space nowrap

    &:focus-visible
      outline 3px solid var(--color-focus)
      outline-offset 3px

@media (min-width: 48rem)
  .resource-detail-page
    padding-block-start var(--space-2xl)

  .resource-detail__header
    grid-template-columns minmax(0, 1.15fr) minmax(0, 0.85fr)
    align-items end

  .resource-detail__layout
    grid-template-columns minmax(0, 1.65fr) minmax(15rem, 0.7fr)

@media (prefers-reduced-motion: reduce)
  .resource-detail__back
    transition-duration 0ms
</style>
